import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import { TokenInterceptor } from "./token.interceptor";

import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { Router } from "@angular/router";

import { ReactiveFormsModule } from "@angular/forms";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { ToastrModule } from "ngx-toastr";



import {
  SiAuthenticationModule,
  SiAuthenticationService,
  ISiAuthenticationConfig,
} from "@strategic-insights/ng-authentication";

import { AuthGuard } from "./auth.guard";
import { SiCustomerAccessComponent } from "./si-customer-access/si-customer-access.component";

import config from "../config/config.json";
import { SharedModule } from "./shared/shared.module";
import { SiCustomerGuard } from "./si-customer.gaurd";
import { AppService } from "./app.service";
import { CookieModule, CookieService } from "ngx-cookie";
import { GlobalErrorHandler } from "./services/si-error-handler.service";
import { SiDataService } from "./services/si-data.service";
import { SiLoginPageConfig } from "@strategic-insights/ng-login-page-kc";
import { SiLoginDisclaimerConfig } from "@strategic-insights/ng-login-page-kc";
import { SiForgotPasswordConfig } from "@strategic-insights/ng-login-page-kc";
import { SiLogoutPageConfig } from "@strategic-insights/ng-login-page-kc";
import { SiLoginPageModule } from "@strategic-insights/ng-login-page-kc";
import { SiLoginPageService } from "@strategic-insights/ng-login-page-kc";
import { SiKeycloakConfigService } from "./services/si-keycloak-config.service";

const siLoginPageConfig: SiLoginPageConfig = new SiLoginPageConfig(
  config["loginPageConfig"]["logoURL"],
  config["loginPageConfig"]["headerText"],
  config["loginPageConfig"]["footerText"],
  config["loginPageConfig"]["styleURL"],
  config["loginPageConfig"]["inviteValidateTokenURL"],
  config["loginPageConfig"]["resetPasswordValidateTokenURL"],
  config["loginPageConfig"]["resetPasswordValidateURL"],
  config["loginPageConfig"]["resetPasswordInitiateURL"],
  config["loginPageConfig"]["resetPasswordURL"],
  config["loginPageConfig"]["activateUserURL"],
  new SiLoginDisclaimerConfig(
    config["loginPageConfig"]["disclaimer"]["isApplied"],
    config["loginPageConfig"]["disclaimer"]["headerText"],
    config["loginPageConfig"]["disclaimer"]["content"],
    config["loginPageConfig"]["disclaimer"]["footerText"]
  ),
  new SiForgotPasswordConfig(
    config["loginPageConfig"]["forgotPassword"]["isApplied"],
    config["loginPageConfig"]["forgotPassword"]["headerText"],
    config["loginPageConfig"]["forgotPassword"]["footerText"],
    config["loginPageConfig"]["forgotPassword"]["branding"]
  ),
  new SiLogoutPageConfig(
    config["loginPageConfig"]["logoutPage"]["logoURL"],
    config["loginPageConfig"]["logoutPage"]["headerText"],
    config["loginPageConfig"]["logoutPage"]["logoutText"],
    config["loginPageConfig"]["logoutPage"]["sessionExpireText"],
    config["loginPageConfig"]["logoutPage"]["footerText"]
  )
);

export function initializeApp(configService: SiKeycloakConfigService) {
  return (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      configService.loadConfig().subscribe(
        () => resolve(),
        error => {
          console.error('Configuration load error:', error);
          reject();
        }
      );
    });
  };
 }

@NgModule({
  declarations: [AppComponent, SiCustomerAccessComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({ maxOpened: 1, preventDuplicates: true }),
    ReactiveFormsModule,
    AppRoutingModule,
    SiAuthenticationModule,
    SharedModule,
    {
      ngModule: SiLoginPageModule,
      providers: [
        { provide: SiLoginPageConfig, useValue: siLoginPageConfig },
        SiLoginPageService,
      ],
    },
    CookieModule.forRoot()
  ],
  providers: [
    SiKeycloakConfigService,
    { provide: APP_INITIALIZER, useFactory: initializeApp, deps: [SiKeycloakConfigService], multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    AuthGuard,
    AppService,
    SiCustomerGuard,
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private cookieService: CookieService,
    private siAuthenticationService: SiAuthenticationService,
    private router: Router,
    private siDataService: SiDataService
  ) {
    const self = this;
    let checkLogin = false;
    let checkSession = false;
    let CONFIG = config["authFRConfig"];
    if (this.cookieService.get("authtype")) {
      CONFIG = config["authSSOConfig"];
    }
    this.siAuthenticationService.init(<ISiAuthenticationConfig>CONFIG);

    self.siAuthenticationService.authStatus.subscribe({
      next: (status) => {
        // If navigating to a standalone route (e.g. auditlog), suppress all redirect logic
        if (sessionStorage.getItem('standaloneRoute')) { return; }

        if (status) {
          if (self.router.url.split("?")[0] !== "/inviteUserForm") {
            checkLogin = true;
            let activeCustomer = self.siAuthenticationService.getActiveCustomer();
            const customerList = self.siAuthenticationService.getCustomerList();

            // Fallback: restore activeCustomer from storage if not set in service
            if (!activeCustomer) {
              activeCustomer = self.cookieService.get('activeCustomer') ||
                              self.cookieService.get('orgname') ||
                              localStorage.getItem('activeCustomer') ||
                              sessionStorage.getItem('activeCustomer');

              if (activeCustomer) {
                self.siAuthenticationService.setActiveCustomer(activeCustomer);
                // Let the activeCustomer subscription handle navigation
                return;
              }
            }

            if (activeCustomer) {
              self.router.navigate([
                self.siAuthenticationService.getUnformattedCustomerName(activeCustomer),
              ]);
            } else if (customerList.length === 0) {
              self.router.navigate(["/unauthorized"]);
            } else if (customerList.length === 1) {
              if (!self.siAuthenticationService.getActiveCustomer()) {
                self.siDataService.clearIndexedDB().subscribe();
              }
              self.siAuthenticationService.setActiveCustomer(customerList[0]);
            } else {
              if (!self.siAuthenticationService.getActiveCustomer()) {
                self.siDataService.clearIndexedDB().subscribe();
              }
              self.router.navigate(["customerAccess"]);
            }
          }
        } else if (
          self.siAuthenticationService.getWebToken() ||
          self.siAuthenticationService.getAuthToken()
        ) {
          if (!sessionStorage.getItem('standaloneRoute')) {
            checkSession = true;
            self.siAuthenticationService.checkTokenNew().subscribe({
              next: (response) => {},
              error: (error) => {
                this.router.navigate(['/kclogoutredirect'], { queryParams: { logout: true } });
              },
            });
          }
        } else {
          // Token is gone — session truly expired
          if (checkLogin) {
            checkLogin = false;
            this.router.navigate(['/kclogoutredirect'], { queryParams: { logout: true } });
          } else if (checkSession && !(self.siAuthenticationService.getWebToken() || self.siAuthenticationService.getAuthToken())) {
            checkSession = false;
            this.router.navigate(['/kclogoutredirect'], { queryParams: { logout: true } });
          }
        }
      },
    });

    self.siAuthenticationService.activeCustomer.subscribe({
      next: (customer) => {
        if (sessionStorage.getItem('standaloneRoute')) { return; }
        if (customer) {
          self.router.navigate([customer]);
        }
      },
    });
  }
}
